// src/screens/PromotionScreen.js for CUSTOMER VIEW (sipnscore-customer)
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import { resolveTeamMember } from "../services/scanPointService";
import { getQRData } from "../services/localStorageService";
import { requestReward } from "../services/voucherService";
import { toast } from "react-toastify";
import { useBrand } from "../hooks/useBrand";
import { safeAssetUrl, brandFrame, assetStyle } from "../config/branding";
import { holderLine } from "../config/holder";
import { useColorScheme } from "../hooks/useColorScheme";

const PromotionScreen = ({ qrData, userData }) => {
  const navigate = useNavigate();
  const [promotionData, setPromotionData] = useState(null);
  const brand = useBrand(qrData?.eventId || null);
  // An image that 404s must not leave a broken icon on a guest's screen, so a
  // failed load switches to the brand panel instead of retrying elsewhere.
  const [bannerFailed, setBannerFailed] = useState(false);
  // The brand's banner takes precedence, then whatever the activation itself
  // carries, which is the field every pre-branding activation still uses.
  const bannerUrl =
    brand.bannerUrl || safeAssetUrl(promotionData?.eventBannerUrl) || null;
  const scheme = useColorScheme();
  // Only the brand's own banner has a measured tone. An activation's legacy
  // bannerImageUrl was never analysed, so it is left alone rather than guessed.
  const bannerFrame = brand.bannerUrl
    ? brandFrame({
        tone: brand.bannerTone,
        transparency: brand.bannerTransparency,
        scheme,
        mode: brand.bannerPlateMode,
      })
    : { background: "transparent", plated: false, fit: null };
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPromotionData = async () => {
      setLoading(true);

      try {
        // Check URL parameters FIRST to ensure fresh data from Firebase
        const queryParams = new URLSearchParams(window.location.search);
        const idParam = queryParams.get("id");
        const dataParam = queryParams.get("data");

        // Handle ID parameter (simplified format)
        if (idParam) {
          const [eventId, bartenderId] = idParam.split("-");

          if (eventId && bartenderId) {
            // Fetch complete event and bartender data from Firebase
            const eventDoc = await getDoc(doc(db, "events", eventId));
            const teamMember = await resolveTeamMember(eventId, bartenderId);

            if (eventDoc.exists()) {
              const eventData = eventDoc.data();

              // A demo that has been stopped in the portal must stop accepting
              // scans straight away, otherwise a printed demo code keeps
              // collecting after the pitch is over.
              if (eventData.isDemo && eventData.demoStopped === true) {
                navigate("/access-denied");
                return;
              }

              const fullData = {
                eventId,
                eventName: eventData.name,
                eventBannerUrl: eventData.eventBannerUrl || "",
                bartenderId,
                bartenderName: teamMember.name,
                teamRoleLabel: teamMember.role,
                teamIsPerson: teamMember.person,
                holderKind: teamMember.holderKind,
                holderName: teamMember.holderName,
                promotion: eventData.promotion || "",
                venue: eventData.venue || "",
                featuredBrands: eventData.featuredBrands || [],
                questionsEnabled: eventData.questionsEnabled !== false,
                startDate: eventData.startDate || eventData.date || null,
                endDate: eventData.endDate || eventData.date || null,
              };

              setPromotionData(fullData);
              setLoading(false);
              return;
            }
          }
        }

        // Handle data parameter (full JSON format)
        else if (dataParam) {
          try {
            const decodedData = JSON.parse(decodeURIComponent(dataParam));
            setPromotionData(decodedData);
            setLoading(false);
            return;
          } catch (error) {
            console.error("Error parsing QR data:", error);
          }
        }

        // If no URL parameters, check if QR data is passed directly
        else if (qrData) {
          setPromotionData(qrData);
          setLoading(false);
          return;
        }

        // Otherwise try to get it from local storage
        else {
          const storedQRData = getQRData();
          if (storedQRData) {
            setPromotionData(storedQRData);
            setLoading(false);
            return;
          }
        }

        // If we've reached this point, we couldn't find any data
        // Redirect to scan screen
        if (!promotionData) {
          setLoading(false);
          navigate("/scan");
        }
      } catch (error) {
        console.error("Error loading promotion data:", error);
        toast.error("Error loading event details. Please try again.");
        setLoading(false);
      }
    };

    loadPromotionData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrData, navigate]);

  // Check if event is within active date range
  // Worked out once rather than called three times in the markup.
  const holderSentence = holderLine({
    kind: promotionData?.holderKind,
    name: promotionData?.holderName,
    roleLabel: promotionData?.teamRoleLabel,
  });

  const isEventActive = () => {
    if (!promotionData) return true;
    const now = new Date();
    try {
      if (promotionData.startDate) {
        const start = promotionData.startDate.toDate
          ? promotionData.startDate.toDate()
          : new Date(promotionData.startDate.seconds * 1000);
        start.setHours(0, 0, 0, 0);
        if (now < start) return false;
      }
      if (promotionData.endDate) {
        const end = promotionData.endDate.toDate
          ? promotionData.endDate.toDate()
          : new Date(promotionData.endDate.seconds * 1000);
        end.setHours(23, 59, 59, 999);
        if (now > end) return false;
      }
    } catch (e) {
      return true; // Fail open
    }
    return true;
  };

  // Keep the activation live while the guest is looking at it.
  //
  // The banner, the promotion copy, the name and the featured brands are
  // subscribed, so a change made in the portal lands on the phone in front of
  // them rather than on the next scan. This is what makes a demo convincing.
  useEffect(() => {
    const eventId = promotionData?.eventId;
    if (!eventId) return undefined;

    const unsubscribe = onSnapshot(
      doc(db, "events", eventId),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();

        // A demo switched off in the portal stops accepting scans immediately.
        if (data.isDemo && data.demoStopped === true) {
          navigate("/access-denied");
          return;
        }

        setPromotionData((prev) =>
          prev
            ? {
                ...prev,
                eventName: data.name ?? prev.eventName,
                eventBannerUrl: data.eventBannerUrl ?? prev.eventBannerUrl,
                promotion: data.promotion ?? prev.promotion,
                venue: data.venue ?? prev.venue,
                featuredBrands: data.featuredBrands ?? prev.featuredBrands,
                questionsEnabled:
                  data.questionsEnabled !== undefined
                    ? data.questionsEnabled
                    : prev.questionsEnabled,
              }
            : prev
        );
      },
      (error) => {
        // A dropped listener must never break the flow the guest is already in.
        console.error("Activation listener error:", error);
      }
    );

    return () => unsubscribe();
  }, [promotionData?.eventId, navigate]);

  const handleContinue = async () => {
    if (!promotionData || !userData) {
      toast.error("Missing event or user data. Please try again.");
      return;
    }

    if (!isEventActive()) {
      toast.error("This event is not currently active.");
      return;
    }

    try {
      // Record the scan in Firestore
      await addDoc(collection(db, "scans"), {
        eventId: promotionData.eventId,
        eventName: promotionData.eventName,
        bartenderId: promotionData.bartenderId,
        bartenderName: promotionData.bartenderName,
        userId: userData.uid,
        userName: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
        userEmail: userData.email,
        timestamp: serverTimestamp(),
        customerCaptured: true,
      });

      // Check if questionnaire is enabled for this event
      if (promotionData.questionsEnabled === false) {
        // Questions disabled - still create voucher claim and send email
        let claimSent = false;
        let limitReached = false;
        try {
          // One server call: it checks the scan limit, the global email cap and
          // whether any codes are left, creates the claim and sends the email.
          const outcome = await requestReward({
            eventId: promotionData.eventId,
            customerId: userData.uid,
            customerEmail: userData.email,
            customerName: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
            bartenderId: promotionData.bartenderId,
          });
          claimSent = outcome.status === "sent";
          limitReached = outcome.status === "limit" || outcome.status === "cap";
        } catch (rewardError) {
          console.error("Reward request error:", rewardError);
        }

        navigate("/thank-you", { state: { claimSent, limitReached } });
      } else {
        navigate("/questionnaire");
      }
    } catch (error) {
      console.error("Error recording scan:", error);
      toast.error("Error recording scan. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading promotion details...</p>
      </div>
    );
  }

  if (!promotionData) {
    return (
      <div className="screen-container">
        <h1 className="screen-title">Error</h1>
        <p>No promotion data found. Please scan a valid QR code.</p>
        <button className="button" onClick={() => navigate("/scan")}>
          Scan QR Code
        </button>
      </div>
    );
  }

  return (
    <div className="screen-container">
      <h1 className="screen-title">Welcome, {userData.firstName}!</h1>

      {/* The banner is the brand's if one is set, otherwise the activation's
          own. When there is neither, a brand coloured panel with the name on it
          reads as intentional. The previous version rendered a debug block onto
          the guest's phone, "No banner URL found" with the raw value under it,
          and fell back to an external placeholder service on an image error,
          which is a third party fetch from a stranger's phone. Both gone. */}
      <div
        className="promotion-banner"
        style={{ background: bannerFrame.background }}
      >
        {bannerUrl && !bannerFailed ? (
          <img
            src={bannerUrl}
            alt=""
            style={{
              ...assetStyle(bannerFrame.fit || brand.bannerFit, brand.bannerScale),
              padding: bannerFrame.plated ? "10px" : 0,
            }}
            onError={() => setBannerFailed(true)}
          />
        ) : (
          <div className="promotion-banner__fallback">
            <span>{brand.name || promotionData.eventName}</span>
          </div>
        )}
      </div>

      <div className="promotion-details">
        <h3>{promotionData.eventName}</h3>
        <p>
          <strong>Venue:</strong> {promotionData.venue}
        </p>
        <div className="promotion-text">{promotionData.promotion}</div>
      </div>

      {promotionData.featuredBrands &&
        promotionData.featuredBrands.length > 0 && (
          <div className="brands-carousel">
            <h3 className="brands-title">Featured Brands</h3>
            <div className="brands-container">
              {promotionData.featuredBrands.map((featured, index) => (
                <div key={index} className="brand-item">
                  {/* No placeholder service. Reaching out to a third party from
                      a guest's phone to render a missing logo is a request we
                      do not control, on a connection we cannot rely on. */}
                  {safeAssetUrl(featured.logoUrl) ? (
                    <img
                      src={safeAssetUrl(featured.logoUrl)}
                      alt=""
                      className="brand-logo"
                    />
                  ) : (
                    <div className="brand-logo brand-logo--none" aria-hidden="true">
                      {(featured.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="brand-name">{featured.name}</div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* One line about whoever is behind the code, or no line at all.
          holderLine returns null for a kind of "none", for an unknown kind, and
          whenever there is no name, so a blank name field cannot produce "Your
          promoter today is" with nothing after it. The sentence lives in
          config/holder.js, mirrored from the portal, so the phone and the
          preview cannot word it differently. */}
      {holderSentence ? (
        <div className="bartender-info">
          <p>{holderSentence}</p>
        </div>
      ) : null}

      {!isEventActive() && (
        <div
          style={{
            background: "#fff3e0",
            border: "1px solid #ffe0b2",
            borderRadius: "8px",
            padding: "12px",
            margin: "12px 0",
            textAlign: "center",
            color: "#e65100",
            fontWeight: "600",
          }}
        >
          This event is not currently active.
        </div>
      )}

      <button className="button" onClick={handleContinue} disabled={!isEventActive()}>
        Next
      </button>
    </div>
  );
};

export default PromotionScreen;
